// File: client/src/components/ActiveEffectsDisplay.js
// Visual indicators of active turn-scoped card effects and remaining moves.

import React from 'react';
import { ShieldAlert, Zap, Award } from 'lucide-react';

const ActiveEffectsDisplay = ({ activeEffects = [], turnState }) => {
  // Read fields from turnState if provided
  const movesAllowed = turnState ? turnState.movesAllowed : 1;
  const movesMade = turnState ? turnState.movesMade : 0;
  const activeRules = turnState && turnState.rules ? turnState.rules : {};

  // Build list of active badges
  const badges = [];

  // 1. Move counter for multi-move turns
  if (movesAllowed > 1) {
    const isCompleted = movesMade >= movesAllowed;
    badges.push({
      id: 'move-counter',
      label: `Moves: ${movesMade} / ${movesAllowed}`,
      color: isCompleted
        ? 'bg-emerald-600 border-emerald-400 text-white'
        : 'bg-blue-600 border-blue-400 text-white animate-pulse',
      icon: <Zap size={14} />,
    });
  }

  // 2. Active rule flags from turnState
  if (activeRules.noSamePieceTwice) {
    badges.push({
      id: 'no-same-piece',
      label: 'Unique Piece Rule',
      color: 'bg-indigo-600/30 border-indigo-500/50 text-indigo-200',
      icon: <ShieldAlert size={14} />,
    });
  }

  if (activeRules.movementOverride) {
    badges.push({
      id: 'movement-override',
      label: `Knight Fever (${activeRules.movementOverride})`,
      color: 'bg-fuchsia-600/30 border-fuchsia-500/50 text-fuchsia-200',
      icon: <Award size={14} />,
    });
  }

  if (activeRules.movementLimit) {
    const { piece, maxDistance } = activeRules.movementLimit;
    badges.push({
      id: 'movement-limit',
      label: `Tiny ${piece} (Max ${maxDistance} squares)`,
      color: 'bg-amber-600/30 border-amber-500/50 text-amber-200',
      icon: <ShieldAlert size={14} />,
    });
  }

  if (activeRules.pawnRiot) {
    badges.push({
      id: 'pawn-riot-active',
      label: 'Pawns Rioting!',
      color: 'bg-red-600/30 border-red-500/50 text-red-200',
      icon: <Zap size={14} />,
    });
  }

  // 3. Active effects list from props (e.g. from previous turn actions)
  activeEffects.forEach((effect, index) => {
    badges.push({
      id: `effect-${index}`,
      label: effect.name || effect.kind,
      color: 'bg-slate-700 border-slate-600 text-slate-200',
      icon: <Zap size={14} />,
    });
  });

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 items-center justify-center my-3 max-w-xl">
      {badges.map((badge) => (
        <span
          key={badge.id}
          className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border shadow-sm ${badge.color}`}
        >
          {badge.icon}
          {badge.label}
        </span>
      ))}
    </div>
  );
};

export default ActiveEffectsDisplay;
